import { FormattedMessage, useIntl } from 'react-intl';

import type { SVGProps } from 'react';

/**
 * Illustration for the Practice downgrade-reconsider modal: four team-role
 * pills (Owner / Admin / Clinician / Assistant) over concentric circles.
 * Converted from the Figma export (downgrade-team-access.svg) so the labels
 * can be translated via react-intl.
 */
// theme-agnostic: fixed-palette Figma illustration, renders identically in both themes
export const DowngradeTeamAccessSvg = (props: SVGProps<SVGSVGElement>) => {
  const intl = useIntl();

  return (
    // direction="ltr": the artwork is composed left-to-right; without it, an
    // inherited dir="rtl" (Arabic) flips text-anchor's start side and every
    // label extends leftward from its authored x, clipping out of the canvas.
    <svg
      viewBox="0 0 613 460"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      direction="ltr"
      role="img"
      aria-label={intl.formatMessage({
        defaultMessage:
          'Illustration of the Owner, Admin, Clinician and Assistant team-role pills',
        id: 'RgBiqdtpu1',
      })}
      {...props}
    >
      <g clipPath="url(#downgrade-team-access-clip0)">
        <rect width="613" height="460" fill="white" />
        <rect
          width="712"
          height="675"
          transform="translate(-49 -108.391)"
          fill="#FCFAF8"
        />
        <circle
          cx="46.2503"
          cy="229.288"
          r="97.9145"
          stroke="#C39DA8"
          strokeWidth="0.984065"
        />
        <circle
          cx="95.4535"
          cy="229.288"
          r="147.118"
          stroke="#C39DA8"
          strokeWidth="0.984065"
        />
        <circle
          cx="144.657"
          cy="229.288"
          r="196.321"
          stroke="#C39DA8"
          strokeWidth="0.984065"
        />
        <circle
          cx="193.86"
          cy="227.32"
          r="245.524"
          stroke="#C39DA8"
          strokeWidth="0.984065"
        />
        <circle
          cx="243.063"
          cy="227.319"
          r="294.728"
          stroke="#C39DA8"
          strokeWidth="0.984065"
        />
        <circle
          cx="292.267"
          cy="227.319"
          r="343.931"
          stroke="#C39DA8"
          strokeWidth="0.984065"
        />
        <rect
          x="43.123"
          y="205.759"
          width="110.509"
          height="49.1908"
          rx="24.5954"
          fill="#28030F"
        />
        <text
          fill="#F9F4F1"
          style={{ whiteSpace: 'pre' }}
          xmlSpace="preserve"
          className="font-exposure"
          fontSize="24.3815"
          letterSpacing="-0.02em"
        >
          <tspan x="59.377" y="238.89">
            <FormattedMessage
              defaultMessage="Owner"
              id="kesVYa+yux"
              description="Team-role label on the first of four pills (Owner, Admin, Clinician, Assistant) in the team-access illustration on the downgrade modal — the practice owner role. Limited space — keep the translation as short as the English or it will overflow"
            />
          </tspan>
        </text>
        <rect
          x="167.178"
          y="205.759"
          width="110.509"
          height="49.1908"
          rx="24.5954"
          fill="#194B22"
        />
        <text
          fill="#F9F4F1"
          style={{ whiteSpace: 'pre' }}
          xmlSpace="preserve"
          className="font-exposure"
          fontSize="24.3815"
          letterSpacing="-0.02em"
        >
          <tspan x="183.432" y="238.89">
            <FormattedMessage
              defaultMessage="Admin"
              id="/VqSpvZZsB"
              description="Team-role label on the second of four pills (Owner, Admin, Clinician, Assistant) in the team-access illustration on the downgrade modal — the administrator role. Limited space — keep the translation as short as the English or it will overflow"
            />
          </tspan>
        </text>
        <rect
          x="291.232"
          y="205.759"
          width="131.509"
          height="49.1908"
          rx="24.5954"
          fill="#B9CFFF"
        />
        <text
          fill="#28030F"
          style={{ whiteSpace: 'pre' }}
          xmlSpace="preserve"
          className="font-exposure"
          fontSize="24.3815"
          letterSpacing="-0.02em"
        >
          <tspan x="307.486" y="238.89">
            <FormattedMessage
              defaultMessage="Clinician"
              id="cWqjsPFclC"
              description="Team-role label on the third of four pills (Owner, Admin, Clinician, Assistant) in the team-access illustration on the downgrade modal — the clinician role. Limited space — keep the translation as short as the English or it will overflow"
            />
          </tspan>
        </text>
        <rect
          x="436.285"
          y="205.759"
          width="134.509"
          height="49.1908"
          rx="24.5954"
          fill="#28030F"
          fillOpacity="0.15"
        />
        <text
          fill="#28030F"
          style={{ whiteSpace: 'pre' }}
          xmlSpace="preserve"
          className="font-exposure"
          fontSize="24.3815"
          letterSpacing="-0.02em"
        >
          <tspan x="452.539" y="238.89">
            <FormattedMessage
              defaultMessage="Assistant"
              id="efQiBTq6pB"
              description="Team-role label on the last of four pills (Owner, Admin, Clinician, Assistant) in the team-access illustration on the downgrade modal — the support-staff role. Limited space — keep the translation as short as the English or it will overflow"
            />
          </tspan>
        </text>
      </g>
      <defs>
        <clipPath id="downgrade-team-access-clip0">
          <rect width="613" height="460" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};
